import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import MainPageContainer from "../../../components/MainPageContainer";
import Tooltip from "../../../components/tooltip";
import { SearchBox } from "../../../components/Search";
import { MetricCard } from "./MetricCard";
import { PeakCard } from "./PeakCard";
import { RankTable, FeederTable } from "./RankTable";
import { NetConsumptionPopup } from "./NetConsumptionPopup";
import { FilterTab } from "../../../components/Filters";
import { formatDateForAPI } from "../../Rajasthan/Utility/const";
import {
  getJvvnlDiscomSubstations,
  getJvvnlFeeders,
  getJvvnlFeederMetrics,
  getJvvnlGenerationMetrics,
  getJvvnlDiscomSubDemandMetrics,
  getJvvnlTranscoDemandMetrics,
  getJvvnlCircleDemandMetrics,
  getJvvnlFeederQuality,
  getJvvnlGroupQuality,
  getJvvnlDiscomSubstationForecast,
  getJvvnlFeederForecast,
  getJvvnlTranscoForecast,
  getJvvnlCircleForecast,
  getJvvnlFeederChart,
  getJvvnlGroupQualityForecast,
} from "../../../Rest_api/RestApi";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { ChartType } from "../../../models/chart_model";
import type { JvvnlFeederChartRow } from "../../../models/jvvnl";
import type {
  JvvnlFeederMetricResult,
  JvvnlGenerationMetricResult,
  JvvnlFeederQualityRow,
  JvvnlGroupQualityRow,
  JvvnlGroupQualityGeneratorRow,
} from "../../../models/jvvnl";

type PillOption = { value: string | number; label: string };

const LOAD_GRANULARITY_OPTIONS = [
  "Circle",
  "Transco",
  "Discom Substation",
  "Feeder",
];

const GRANULARITY_LEVELS: Record<string, number> = {
  Circle: 1,
  Transco: 2,
  "Discom Substation": 3,
  Feeder: 4,
};

function roundMw(v: string | number | null | undefined): number {
  return parseFloat(String(v ?? 0)) || 0;
}
function roundMwh(v: string | number | null | undefined): number {
  return parseFloat(String(v ?? 0)) || 0;
}

// API now emits kW/kWh at feeder & discom_sub, MW/MWh at transco/circle/jvvnl.
// Mapper stores raw numeric value in *_mw fields regardless of source unit, so
// the unit label must be selected per granularity.
function unitsFor(granularity: string): {
  loadUnit: string;
  energyUnit: string;
} {
  if (granularity === "Feeder" || granularity === "Discom Substation") {
    return { loadUnit: "kW", energyUnit: "kWh" };
  }
  return { loadUnit: "MW", energyUnit: "MWh" };
}

type WeatherStats = {
  temp?: number | null;
  humid?: number | null;
  solar?: number | null;
};

function toWeatherStats(
  w:
    | {
        temp?: string | null;
        humidity?: string | null;
        solar_irradiance?: string | null;
      }
    | null
    | undefined,
): WeatherStats | null {
  if (!w) return null;
  return {
    temp: w.temp != null ? parseFloat(w.temp) : null,
    humid: w.humidity != null ? parseFloat(w.humidity) : null,
    solar: w.solar_irradiance != null ? parseFloat(w.solar_irradiance) : null,
  };
}

// ─── Skeletons ──────────────────────────────────────────────────────────────

function MetricCardSkeleton() {
  return (
    <div className="relative bg-white rounded-xl border border-[#DFEAF2] p-3 pl-5 flex flex-col gap-2">
      <div className="absolute left-0 top-[15%] bottom-[15%] w-[4px] rounded-full bg-[#DFEAF2]" />
      {/* title */}
      <div className="h-3 w-16 bg-[#DFEAF2] rounded" />
      {/* peak / avg cols */}
      <div className="flex gap-3">
        <div className="flex-1 space-y-1.5">
          <div className="flex gap-1.5">
            <div className="h-2 w-8 bg-[#DFEAF2] rounded" />
            <div className="h-2 w-20 bg-[#EEF4F8] rounded" />
          </div>
          <div className="h-5 w-20 bg-[#DFEAF2] rounded" />
          <div className="h-2 w-14 bg-[#EEF4F8] rounded" />
        </div>
        <div className="w-px bg-[#DFEAF2] self-stretch" />
        <div className="flex-1 space-y-1.5">
          <div className="h-2 w-10 bg-[#DFEAF2] rounded" />
          <div className="h-5 w-20 bg-[#DFEAF2] rounded" />
          <div className="h-2 w-14 bg-[#EEF4F8] rounded" />
        </div>
      </div>
      {/* weather section */}
      <div className="pt-1 space-y-2">
        <div className="flex items-center gap-2">
          <div className="h-2 w-20 bg-[#DFEAF2] rounded" />
          <div className="flex-1 h-px bg-[#DFEAF2]" />
        </div>
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex-1 space-y-1">
              <div className="h-2 w-8 bg-[#DFEAF2] rounded" />
              <div className="h-3 w-12 bg-[#DFEAF2] rounded" />
              <div className="h-2 w-10 bg-[#EEF4F8] rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PeakCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-[#DFEAF2] p-3 flex flex-col gap-3">
      {/* title + date badge on left, value on right */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-20 bg-[#DFEAF2] rounded" />
          <div className="h-4 w-24 bg-[#EEF4F8] rounded-md" />
        </div>
        <div className="h-5 w-16 bg-[#DFEAF2] rounded" />
      </div>
      <div className="w-full h-px bg-[#DFEAF2]" />
      {/* weather stats */}
      <div className="flex justify-between mt-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col gap-1">
            <div className="h-2 w-10 bg-[#DFEAF2] rounded" />
            <div className="h-4 w-14 bg-[#DFEAF2] rounded" />
            <div className="h-2 w-10 bg-[#EEF4F8] rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

function RankTableSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-[#DFEAF2] p-3 flex flex-col gap-3">
      <div className="h-4 w-40 bg-[#DFEAF2] rounded" />
      {[0, 1, 2].map((i) => (
        <div key={i} className="grid grid-cols-5 gap-2">
          <div className="h-3 w-full bg-[#DFEAF2] rounded col-span-2" />
          <div className="h-3 w-full bg-[#EEF4F8] rounded" />
          <div className="h-3 w-full bg-[#EEF4F8] rounded" />
          <div className="h-3 w-full bg-[#EEF4F8] rounded" />
        </div>
      ))}
    </div>
  );
}

// ─── Header (entity filters) ────────────────────────────────────────────────

function Header({
  loadGranularity,
  onGranularityChange,
  selectedLabel,
  onTitleClick,
}: {
  loadGranularity: string;
  onGranularityChange: (v: string) => void;
  selectedLabel?: string | null;
  onTitleClick?: () => void;
}) {
  return (
    <div className="bg-white flex justify-between px-4 items-end rounded-xl border border-[#DFEAF2] mb-4">
      <div className="flex items-center gap-2 py-2">
        {selectedLabel && onTitleClick ? (
          <button
            onClick={onTitleClick}
            className="text-[15px] font-medium text-[#525062] inline-flex items-center gap-1.5 hover:text-[#14171F] transition-colors"
          >
            {selectedLabel}
            <span className="text-[#8C8A96] font-normal">
              ({loadGranularity})
            </span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 3h6v6" />
              <path d="M10 14 21 3" />
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            </svg>
          </button>
        ) : (
          <span className="text-[15px] font-medium text-[#525062]">
            {selectedLabel || loadGranularity}
            {selectedLabel && (
              <span className="text-[#8C8A96] font-normal ml-1">
                ({loadGranularity})
              </span>
            )}
          </span>
        )}
      </div>
      <div className="flex items-end gap-4">
        {LOAD_GRANULARITY_OPTIONS.map((opt) => (
          <button
            key={opt}
            onClick={() => onGranularityChange(opt)}
            className={`text-[13px] font-medium transition-colors whitespace-nowrap pb-2 border-b-2 -mb-px ${
              opt === loadGranularity
                ? "text-primary border-primary"
                : "text-[#6F6D7C] border-transparent hover:text-[#14171F]"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

const RANK_TABS = ["Demand", "Generation"] as const;
type RankTab = (typeof RANK_TABS)[number];
const METRIC_TABS = ["Actual", "Forecast"] as const;
type MetricTab = (typeof METRIC_TABS)[number];
const FEEDER_VIEW_TABS = ["Table", "Chart"];

// ─── Page ───────────────────────────────────────────────────────────────────

const JvvnlMetric = () => {
  const [loadGranularity, setLoadGranularity] = useState("Circle");
  const [rankTab, setRankTab] = useState<RankTab>("Demand");
  const [metricTab, setMetricTab] = useState<MetricTab>("Actual");
  const [feederView, setFeederView] = useState("Table");
  const [primaryWeather, setPrimaryWeather] = useState("Avg Temp");
  const [modalEntity, setModalEntity] = useState<{
    level: "transco" | "discom_sub" | "plant" | "circle" | "feeder";
    id: number | string;
    label: string;
  } | null>(null);

  // Dates from redux (NewDynamicTab seeds it via JvvnlOverview.Metric)
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const range =
    DefaultDates?.Metric?.defaultRangeDate ??
    DefaultDates?.Overview?.defaultRangeDate;
  const dateParams = useMemo(() => {
    if (!range?.startDate || !range?.endDate) return null;
    return {
      startDate: formatDateForAPI(range.startDate),
      endDate: formatDateForAPI(range.endDate),
    };
  }, [range?.startDate, range?.endDate]);

  // Entity dropdowns
  const [allDiscomSubs, setAllDiscomSubs] = useState<any[]>([]);
  const [allFeeders, setAllFeeders] = useState<any[]>([]);
  const [transcoList, setTranscoList] = useState<PillOption[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  const circleList: PillOption[] = [{ value: "KOTPUTLI", label: "KOTPUTLI" }];

  const [circle, setCircle] = useState<PillOption | null>({
    value: "KOTPUTLI",
    label: "KOTPUTLI",
  });
  const [transco, setTransco] = useState<PillOption | null>(null);
  const [discom, setDiscom] = useState<PillOption | null>(null);
  const [feeder, setFeeder] = useState<PillOption | null>(null);

  const ALLOWED_TRANSCO = [
    "BANSUR",
    "BEHROR",
    "KOTPUTLI",
    "MANOHARPUR",
    "NIMRANA",
  ];

  // Discom list filtered by selected transco
  const discomList: PillOption[] = useMemo(() => {
    const filtered = transco
      ? allDiscomSubs.filter((d) => d.transco_substation_id === transco.value)
      : allDiscomSubs;
    return filtered.map((d) => ({
      value: d.discom_substation_id,
      label: d.discom_substation_name,
    }));
  }, [allDiscomSubs, transco]);

  // Feeder list filtered by selected discom, or by selected transco's discoms
  const feederList: PillOption[] = useMemo(() => {
    let filtered = allFeeders;
    if (discom) {
      filtered = allFeeders.filter(
        (f) => f.discom_substation_id === discom.value,
      );
    } else if (transco) {
      const discomIds = new Set(
        allDiscomSubs
          .filter((d) => d.transco_substation_id === transco.value)
          .map((d) => d.discom_substation_id),
      );
      filtered = allFeeders.filter((f) =>
        discomIds.has(f.discom_substation_id),
      );
    }
    return filtered.map((f) => ({
      value: f.feeder_id,
      label: f.feeder_name || String(f.feeder_id),
    }));
  }, [allFeeders, allDiscomSubs, discom, transco]);

  useEffect(() => {
    // Discom/feeder entity lists only needed at Transco, Discom Substation, or Feeder granularity
    if (loadGranularity === "Circle") return;
    // Skip if we already have the data
    if (allDiscomSubs.length > 0 && allFeeders.length > 0) return;

    let mounted = true;
    setLoadingEntities(true);
    const needFeeders =
      loadGranularity === "Feeder" ||
      loadGranularity === "Discom Substation" ||
      loadGranularity === "Transco";
    Promise.all([
      allDiscomSubs.length === 0
        ? getJvvnlDiscomSubstations().catch(() => [])
        : Promise.resolve(allDiscomSubs),
      needFeeders && allFeeders.length === 0
        ? getJvvnlFeeders().catch(() => [])
        : Promise.resolve(allFeeders),
    ])
      .then(([discoms, feeders]) => {
        if (!mounted) return;
        setAllDiscomSubs(discoms);
        setAllFeeders(feeders);
        // Build transco list from discom data — unique transcos filtered to allowed set
        const seen = new Set<number>();
        const transcos: PillOption[] = [];
        for (const d of discoms as any[]) {
          if (
            d.transco_substation_id &&
            !seen.has(d.transco_substation_id) &&
            ALLOWED_TRANSCO.includes(
              (d.transco_substation_name || "").toUpperCase(),
            )
          ) {
            seen.add(d.transco_substation_id);
            transcos.push({
              value: d.transco_substation_id,
              label: d.transco_substation_name,
            });
          }
        }
        setTranscoList(transcos);
      })
      .finally(() => mounted && setLoadingEntities(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadGranularity]);

  // Cascade selection down to children of current granularity.
  // If a parent is selected (e.g. Transco at Discom Substation granularity),
  // expand to all children under that parent. Omit when nothing selected.
  const selectedEntityIds = useMemo<Array<number> | undefined>(() => {
    if (loadGranularity === "Circle") return undefined;
    if (loadGranularity === "Transco") {
      if (transco) return [Number(transco.value)];
      return undefined;
    }
    if (loadGranularity === "Discom Substation") {
      if (discom) return [Number(discom.value)];
      if (transco) {
        const ids = allDiscomSubs
          .filter((d) => d.transco_substation_id === transco.value)
          .map((d) => Number(d.discom_substation_id))
          .filter((n) => !isNaN(n));
        return ids.length ? ids : undefined;
      }
      return undefined;
    }
    if (loadGranularity === "Feeder") {
      if (feeder) return [Number(feeder.value)];
      if (discom) {
        const ids = allFeeders
          .filter((f) => f.discom_substation_id === discom.value)
          .map((f) => Number(f.feeder_id))
          .filter((n) => !isNaN(n));
        return ids.length ? ids : undefined;
      }
      if (transco) {
        const discomIds = new Set(
          allDiscomSubs
            .filter((d) => d.transco_substation_id === transco.value)
            .map((d) => d.discom_substation_id),
        );
        const ids = allFeeders
          .filter((f) => discomIds.has(f.discom_substation_id))
          .map((f) => Number(f.feeder_id))
          .filter((n) => !isNaN(n));
        return ids.length ? ids : undefined;
      }
      return undefined;
    }
    return undefined;
  }, [loadGranularity, transco, discom, feeder, allDiscomSubs, allFeeders]);

  // ── Data state ──
  const [groupDemand, setGroupDemand] =
    useState<JvvnlFeederMetricResult | null>(null);
  const [forecastDemand, setForecastDemand] =
    useState<JvvnlFeederMetricResult | null>(null);
  const [forecastDate, setForecastDate] = useState<string | null>(null);
  const [genResults, setGenResults] = useState<JvvnlGenerationMetricResult[]>(
    [],
  );
  const [feederQualityHigh, setFeederQualityHigh] = useState<
    JvvnlFeederMetricResult[]
  >([]);
  const [feederQualityLow, setFeederQualityLow] = useState<
    JvvnlFeederMetricResult[]
  >([]);

  const [feederMetric, setFeederMetric] =
    useState<JvvnlFeederMetricResult | null>(null);
  const [groupQuality, setGroupQuality] = useState<{
    top: JvvnlGroupQualityRow[];
    bottom: JvvnlGroupQualityRow[];
    top_generators: JvvnlGroupQualityGeneratorRow[];
    bottom_generators: JvvnlGroupQualityGeneratorRow[];
  }>({ top: [], bottom: [], top_generators: [], bottom_generators: [] });
  const [forecastGroupQuality, setForecastGroupQuality] = useState<{
    top: JvvnlGroupQualityRow[];
    bottom: JvvnlGroupQualityRow[];
    top_generators: JvvnlGroupQualityGeneratorRow[];
    bottom_generators: JvvnlGroupQualityGeneratorRow[];
  }>({ top: [], bottom: [], top_generators: [], bottom_generators: [] });
  const [loadingForecastRank, setLoadingForecastRank] = useState(false);
  const [feederQuality, setFeederQuality] = useState<{
    high: JvvnlFeederQualityRow[];
    low: JvvnlFeederQualityRow[];
  }>({ high: [], low: [] });

  const [feederChartRows, setFeederChartRows] = useState<JvvnlFeederChartRow[]>(
    [],
  );
  const [loadingFeederChart, setLoadingFeederChart] = useState(false);

  const [loadingCombined, setLoadingCombined] = useState(false);
  const [loadingRank, setLoadingRank] = useState(false);
  const [loadingFeederQuality, setLoadingFeederQuality] = useState(false);

  // ── Fetch: feeder metric cards (Feeder granularity) ──
  useEffect(() => {
    if (!dateParams) return;
    if (loadGranularity !== "Feeder") {
      setFeederMetric(null);
      setLoadingCombined(false);
      return;
    }
    let cancelled = false;
    setLoadingCombined(true);
    getJvvnlFeederMetrics({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      level: "feeder",
      ids: selectedEntityIds,
      // Auto-combine on server when ids omitted or has 2+ entries.
    })
      .then((res) => {
        if (cancelled) return;
        const first = res?.results?.[0];
        setFeederMetric(first ?? null);
        setLoadingCombined(false);
      })
      .catch(() => {
        if (cancelled) return;
        setFeederMetric(null);
        setLoadingCombined(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateParams, loadGranularity, selectedEntityIds]);

  // ── Fetch: combined (top metric + peak cards) + generation total ──
  useEffect(() => {
    if (!dateParams) return;
    if (loadGranularity === "Feeder") return;
    let mounted = true;
    setLoadingCombined(true);
    const ids: Array<number | string> | undefined = selectedEntityIds;

    // Auto-combine: 4 demand tabs cumulate server-side on no-ids or 2+ ids.
    const groupFetcher =
      loadGranularity === "Circle"
        ? getJvvnlCircleDemandMetrics({
            startDate: dateParams.startDate,
            endDate: dateParams.endDate,
            level: "circle",
            ids,
          })
        : loadGranularity === "Transco"
          ? getJvvnlTranscoDemandMetrics({
              startDate: dateParams.startDate,
              endDate: dateParams.endDate,
              level: "transco",
              ids,
            })
          : getJvvnlDiscomSubDemandMetrics({
              startDate: dateParams.startDate,
              endDate: dateParams.endDate,
              level: "discom_sub",
              ids,
            });

    Promise.all([
      getJvvnlGenerationMetrics({
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        level: "plant",
      }).catch(() => null),
      groupFetcher.catch(() => null),
    ])
      .then(([gen, grp]) => {
        if (!mounted) return;
        setGenResults(gen?.results ?? []);
        setGroupDemand(grp?.results?.[0] ?? null);
      })
      .finally(() => mounted && setLoadingCombined(false));

    return () => {
      mounted = false;
    };
  }, [dateParams, loadGranularity, selectedEntityIds]);

  // ── Fetch: forecast demand ──
  // Each granularity has its own dedicated forecast endpoint with the right
  // model + units. discom_sub still uses /discom-substation-forecast/.
  useEffect(() => {
    let mounted = true;
    const ids = selectedEntityIds;
    const dateArgs = dateParams
      ? { startDate: dateParams.startDate, endDate: dateParams.endDate }
      : {};
    const fetcher =
      loadGranularity === "Feeder"
        ? getJvvnlFeederForecast({ ids, ...dateArgs })
        : loadGranularity === "Transco"
          ? getJvvnlTranscoForecast({ ids, ...dateArgs })
          : loadGranularity === "Circle"
            ? getJvvnlCircleForecast({ ids, ...dateArgs })
            : getJvvnlDiscomSubstationForecast({
                level: "discom_sub",
                ids,
                ...dateArgs,
              });
    fetcher
      .catch(() => null)
      .then((demandRes) => {
        if (!mounted) return;
        setForecastDemand(demandRes?.results?.[0] ?? null);
        setForecastDate(demandRes?.forecast_date ?? null);
      });
    return () => {
      mounted = false;
    };
  }, [loadGranularity, selectedEntityIds, dateParams]);

  // ── Fetch: rank tables (top/bottom 3) ──
  // Both Demand and Generation tabs source from /group-quality/ — demand uses
  // top/bottom, generation uses top_generators/bottom_generators.
  useEffect(() => {
    if (!dateParams) return;
    if (loadGranularity === "Feeder") return;
    if (
      loadGranularity !== "Circle" &&
      loadGranularity !== "Transco" &&
      loadGranularity !== "Discom Substation"
    )
      return;
    let mounted = true;
    setLoadingRank(true);
    const groupLevel: "circle" | "transco" | "discom_sub" =
      loadGranularity === "Circle"
        ? "circle"
        : loadGranularity === "Transco"
          ? "transco"
          : "discom_sub";
    getJvvnlGroupQuality({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      level: groupLevel,
      limit: 3,
      ids: selectedEntityIds,
    })
      .catch(() => null)
      .then((res) => {
        if (!mounted) return;
        setGroupQuality({
          top: res?.top ?? [],
          bottom: res?.bottom ?? [],
          top_generators: res?.top_generators ?? [],
          bottom_generators: res?.bottom_generators ?? [],
        });
      })
      .finally(() => mounted && setLoadingRank(false));
    return () => {
      mounted = false;
    };
  }, [dateParams, loadGranularity, selectedEntityIds]);

  // ── Fetch: forecast rank tables (top/bottom via /group-quality-forecast/) ──
  useEffect(() => {
    if (metricTab !== "Forecast") return;
    if (loadGranularity === "Feeder") {
      setForecastGroupQuality({
        top: [],
        bottom: [],
        top_generators: [],
        bottom_generators: [],
      });
      return;
    }
    let mounted = true;
    setLoadingForecastRank(true);
    const forecastLevel: "circle" | "transco" | "discom_sub" =
      loadGranularity === "Circle"
        ? "circle"
        : loadGranularity === "Discom Substation"
          ? "discom_sub"
          : "transco";
    getJvvnlGroupQualityForecast({
      level: forecastLevel,
      limit: 3,
      ids: selectedEntityIds,
      ...(dateParams
        ? { startDate: dateParams.startDate, endDate: dateParams.endDate }
        : {}),
    })
      .catch(() => null)
      .then((res) => {
        if (!mounted) return;
        setForecastGroupQuality({
          top: res?.top ?? [],
          bottom: res?.bottom ?? [],
          top_generators: res?.top_generators ?? [],
          bottom_generators: res?.bottom_generators ?? [],
        });
      })
      .finally(() => mounted && setLoadingForecastRank(false));
    return () => {
      mounted = false;
    };
  }, [metricTab, loadGranularity, selectedEntityIds, dateParams]);

  // ── Fetch: feeder data-quality tables (via /feeder-quality/) ──
  useEffect(() => {
    if (!dateParams) return;
    if (loadGranularity !== "Feeder") return;
    let mounted = true;
    setLoadingFeederQuality(true);
    getJvvnlFeederQuality({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      high_threshold: 80,
      low_threshold: 20,
      limit: 3,
    })
      .catch(() => null)
      .then((res) => {
        if (!mounted) return;
        setFeederQuality({
          high: res?.above_threshold ?? [],
          low: res?.below_threshold ?? [],
        });
        setFeederQualityHigh([]);
        setFeederQualityLow([]);
      })
      .finally(() => mounted && setLoadingFeederQuality(false));
    return () => {
      mounted = false;
    };
  }, [dateParams, loadGranularity]);

  // ── Fetch: feeder chart (top-N feeders by energy) ──
  useEffect(() => {
    if (!dateParams) return;
    if (loadGranularity !== "Feeder") {
      setFeederChartRows([]);
      return;
    }
    let mounted = true;
    setLoadingFeederChart(true);
    getJvvnlFeederChart({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
    })
      .catch(() => null)
      .then((res) => {
        if (!mounted) return;
        setFeederChartRows(res?.results ?? []);
      })
      .finally(() => mounted && setLoadingFeederChart(false));
    return () => {
      mounted = false;
    };
  }, [dateParams, loadGranularity]);

  // ── Derive feeder chart echarts data ──
  const feederChartData = useMemo(() => {
    if (!feederChartRows.length) return { data: [], legends: [] };
    const data = feederChartRows.map((r) => ({
      name: r.name,
      "Total Energy": r.total_kwh,
    }));
    return {
      data,
      legends: [
        {
          dataKey: "Total Energy",
          name: "Total Energy",
          unit: "kWh",
          type: ChartType.Bar,
        },
      ],
    };
  }, [feederChartRows]);

  const { loadUnit, energyUnit } = useMemo(
    () => unitsFor(loadGranularity),
    [loadGranularity],
  );

  // ── Derive metric cards (Demand, Total Energy, Total Gen, Net Load) ──
  const metricCards = useMemo(() => {
    const demandFull =
      loadGranularity === "Feeder"
        ? feederMetric?.metrics.full
        : groupDemand?.metrics.full;
    const genFullPerPlant = genResults.map((g) => g.metrics.full);
    const totalGenAvgMw = genFullPerPlant.reduce(
      (s, m) => s + roundMw(m?.avg_mw ?? 0),
      0,
    );
    const totalGenPeakMw = genFullPerPlant.reduce(
      (s, m) => s + roundMw(m?.peak_mw ?? 0),
      0,
    );
    const isFeeder = loadGranularity === "Feeder";
    const nullableRound = (
      v: string | number | null | undefined,
    ): number | null => {
      if (v == null) return null;
      const n = parseFloat(String(v));
      return isNaN(n) ? null : n;
    };
    const demandPeak = nullableRound(demandFull?.peak_mw);
    const demandAvg = nullableRound(demandFull?.avg_mw);
    const totalEnergy = nullableRound(demandFull?.total_mwh);
    // Prefer per-bucket generation from group-demand endpoint (/circle/, /transco/, /discom-sub/);
    // fall back to summed plant /generation/ rows (used at Feeder granularity).
    const gPeakGen = groupDemand?.metrics.full?.peak_generation_mw;
    const gAvgGen = groupDemand?.metrics.full?.avg_generation_mw;
    // When groupDemand exists, trust its generation values (null = no generation).
    // Only fall back to summed plant rows at Feeder granularity (no group endpoint).
    const totalGenMW: number | null = isFeeder
      ? genFullPerPlant.length > 0
        ? roundMw(totalGenPeakMw)
        : null
      : groupDemand != null
        ? nullableRound(gPeakGen)
        : genFullPerPlant.length > 0
          ? roundMw(totalGenPeakMw)
          : null;
    const totalGenAvgMW: number | null = isFeeder
      ? genFullPerPlant.length > 0
        ? roundMw(totalGenAvgMw)
        : null
      : groupDemand != null
        ? nullableRound(gAvgGen)
        : genFullPerPlant.length > 0
          ? roundMw(totalGenAvgMw)
          : null;
    // Prefer server-computed net (per-bucket from group-demand endpoint) when available
    const gPeakNet = groupDemand?.metrics.full?.peak_net_mw;
    const gAvgNet = groupDemand?.metrics.full?.avg_net_mw;
    const netPeak: number | null = isFeeder
      ? demandPeak != null && totalGenMW != null
        ? demandPeak - totalGenMW
        : null
      : groupDemand != null
        ? nullableRound(gPeakNet)
        : demandPeak != null && totalGenMW != null
          ? demandPeak - totalGenMW
          : null;
    const netAvg: number | null = isFeeder
      ? demandAvg != null && totalGenAvgMW != null
        ? demandAvg - totalGenAvgMW
        : null
      : groupDemand != null
        ? nullableRound(gAvgNet)
        : demandAvg != null && totalGenAvgMW != null
          ? demandAvg - totalGenAvgMW
          : null;

    const genFull = genResults[0]?.metrics.full;
    // Group-demand carries its own generation metadata (date/slot/weather);
    // prefer those at non-Feeder granularity.
    const groupGenDate = !isFeeder
      ? groupDemand?.metrics.full?.peak_generation_date
      : null;
    const groupGenSlot = !isFeeder
      ? groupDemand?.metrics.full?.peak_generation_slot_48
      : null;
    const groupGenPeakWeather = !isFeeder
      ? groupDemand?.metrics.full?.peak_generation_weather
      : null;
    const base = [
      {
        title: "Energy",
        peakValue: totalEnergy,
        averageValue: nullableRound(demandFull?.avg_mwh),
        unit: energyUnit,
        peakLabel: "Total",
        accentColor: "#2563EB",
        averageColor: "#2563EB",
        peakWeather: toWeatherStats(demandFull?.peak_weather),
        avgWeather: toWeatherStats(demandFull?.avg_weather),
      },
      {
        title: "Load",
        peakValue: demandPeak,
        averageValue: demandAvg,
        unit: loadUnit,
        accentColor: "#E25A1F",
        averageColor: "#E25A1F",
        peakDate: demandFull?.peak_date,
        peakSlot: demandFull?.peak_slot_48 ?? null,
        peakWeather: toWeatherStats(demandFull?.peak_weather),
        // Load tab shows peak_weather in the main weather row (not avg_weather).
        avgWeather: toWeatherStats(demandFull?.peak_weather),
      },
      {
        title: "Generation",
        peakValue: totalGenMW,
        averageValue: totalGenAvgMW,
        unit: loadUnit,
        accentColor: "#606679",
        averageColor: "#606679",
        peakDate:
          groupGenDate ?? genFull?.peak_generation_date ?? genFull?.peak_date,
        peakSlot:
          groupGenSlot ??
          genFull?.peak_generation_slot_48 ??
          genFull?.peak_slot_48 ??
          null,
        peakWeather:
          toWeatherStats(groupGenPeakWeather) ??
          toWeatherStats(genFull?.peak_weather),
        // Generation tab shows peak_generation_weather in the main row.
        avgWeather:
          toWeatherStats(groupGenPeakWeather) ??
          toWeatherStats(genFull?.peak_weather),
      },
      {
        title: "Net Load",
        peakValue: netPeak,
        averageValue: netAvg,
        unit: loadUnit,
        accentColor: "#3A7E54",
        averageColor: "#3A7E54",
        peakDate:
          netPeak != null
            ? (demandFull?.peak_net_date ?? demandFull?.peak_date)
            : undefined,
        peakSlot:
          netPeak != null
            ? (demandFull?.peak_net_slot_48 ?? demandFull?.peak_slot_48 ?? null)
            : null,
        peakWeather: toWeatherStats(
          demandFull?.peak_net_weather ?? demandFull?.peak_weather,
        ),
        // Net Load tab shows peak_net_weather in the main row.
        avgWeather: toWeatherStats(
          demandFull?.peak_net_weather ?? demandFull?.peak_weather,
        ),
      },
    ];
    return base;
  }, [
    genResults,
    loadGranularity,
    feederMetric,
    groupDemand,
    loadUnit,
    energyUnit,
  ]);

  // ── Derive peak cards (Morning/Solar/Evening) ──
  const peakCards = useMemo(() => {
    const m =
      loadGranularity === "Feeder"
        ? feederMetric?.metrics
        : groupDemand?.metrics;
    const toVal = (mw: string | number | undefined | null) =>
      mw == null ? null : roundMw(mw);
    const unit = loadUnit;
    return [
      {
        title: "Morning Peak",
        peakBlock: m?.morning?.peak_slot_48 ?? null,
        peakDate: m?.morning?.peak_date ?? null,
        peakValue: toVal(m?.morning?.peak_mw),
        unit,
        // FRONTEND_API 7: per-bucket avg_weather removed; show peak_weather.
        weather: toWeatherStats(
          m?.morning?.avg_weather ?? m?.morning?.peak_weather,
        ),
        weatherPrevDay: null,
      },
      {
        title: "Solar Peak",
        peakBlock: m?.solar?.peak_slot_48 ?? null,
        peakDate: m?.solar?.peak_date ?? null,
        peakValue: toVal(m?.solar?.peak_mw),
        unit,
        weather: toWeatherStats(
          m?.solar?.avg_weather ?? m?.solar?.peak_weather,
        ),
        weatherPrevDay: null,
      },
      {
        title: "Evening Peak",
        peakBlock: m?.evening?.peak_slot_48 ?? null,
        peakDate: m?.evening?.peak_date ?? null,
        peakValue: toVal(m?.evening?.peak_mw),
        unit,
        weather: toWeatherStats(
          m?.evening?.avg_weather ?? m?.evening?.peak_weather,
        ),
        weatherPrevDay: null,
      },
    ];
  }, [groupDemand, feederMetric, loadGranularity, loadUnit]);

  // ── Derive rank tables (top 3 / bottom 3) ──
  const rankRows = useMemo(() => {
    const toDemandRow = (r: JvvnlGroupQualityRow) => ({
      entity: r.name || `#${r.entity_id}`,
      entityId: r.entity_id,
      avgDemand: roundMw(r.avg_demand_mw ?? 0),
      peakDemand: roundMw(r.peak_demand_mw ?? 0),
      avgTemp: r.avg_temp ?? 0,
      avgHumidity: r.avg_humidity ?? null,
      avgSolarIrradiance: r.avg_solar_irradiance ?? null,
      netLoad: roundMwh(r.total_demand_mwh ?? 0),
      peakDate: r.peak_demand_date ?? null,
      peakSlot: r.peak_demand_slot_48 ?? null,
    });

    const toGenRow = (r: JvvnlGroupQualityGeneratorRow) => ({
      entity: r.name || `#${r.entity_id}`,
      entityId: r.entity_id,
      avgDemand: parseFloat(String(r.avg_generation_mw ?? 0)) || 0,
      peakDemand: parseFloat(String(r.peak_generation_mw ?? 0)) || 0,
      avgTemp: r.avg_temp ?? null,
      avgHumidity: r.avg_humidity ?? null,
      avgSolarIrradiance: r.avg_solar_irradiance ?? null,
      netLoad: 0,
      peakDate: r.peak_generation_date ?? null,
      peakSlot: r.peak_generation_slot_48 ?? null,
      pctLoadCovered:
        r.pct_load_covered != null
          ? isNaN(parseFloat(String(r.pct_load_covered)))
            ? null
            : parseFloat(String(r.pct_load_covered))
          : null,
    });

    // Forecast tab: use /group-quality-forecast/ data for both Demand and Generation
    if (metricTab === "Forecast") {
      if (rankTab === "Generation") {
        return {
          top: forecastGroupQuality.top_generators.map(toGenRow),
          bottom: forecastGroupQuality.bottom_generators.map(toGenRow),
        };
      }
      return {
        top: forecastGroupQuality.top.map(toDemandRow),
        bottom: forecastGroupQuality.bottom.map(toDemandRow),
      };
    }

    // Generation tab: use top_generators/bottom_generators from /group-quality/
    if (rankTab === "Generation") {
      return {
        top: groupQuality.top_generators.map(toGenRow),
        bottom: groupQuality.bottom_generators.map(toGenRow),
      };
    }

    // Demand tab: use server-sorted top/bottom from /group-quality/
    return {
      top: groupQuality.top.map(toDemandRow),
      bottom: groupQuality.bottom.map(toDemandRow),
    };
  }, [groupQuality, forecastGroupQuality, metricTab, rankTab]);

  const feederQualityRows = useMemo(() => {
    // Prefer server /feeder-quality/ rows when present
    if (feederQuality.high.length || feederQuality.low.length) {
      const toRow = (r: JvvnlFeederQualityRow) => ({
        entity: r.name || `#${r.entity_id}`,
        entityId: r.entity_id,
        availablePct: Number(r.available_pct.toFixed(2)),
        zerosPct: Number(r.zeros_pct.toFixed(2)),
        nansPct: Number(r.nulls_pct.toFixed(2)),
      });
      return {
        high: feederQuality.high.map(toRow),
        low: feederQuality.low.map(toRow),
      };
    }
    const toRow = (r: JvvnlFeederMetricResult) => ({
      entity: r.name || `#${r.entity_id}`,
      entityId: r.entity_id,
      availablePct: Number(
        (
          100 -
          (r.quality?.zero_data_pct ?? 0) -
          (r.quality?.kwh_modified_pct ?? 0)
        ).toFixed(2),
      ),
      zerosPct: Number((r.quality?.zero_data_pct ?? 0).toFixed(2)),
      nansPct: Number((r.quality?.kwh_modified_pct ?? 0).toFixed(2)),
    });
    return {
      high: feederQualityHigh.map(toRow),
      low: feederQualityLow.map(toRow),
    };
  }, [feederQualityHigh, feederQualityLow, feederQuality]);

  // Reset entity selections when changing granularity
  useEffect(() => {
    setTransco(null);
    setDiscom(null);
    setFeeder(null);
  }, [loadGranularity]);

  return (
    <>
      <div className="gas-page-container">
        <MainPageContainer className="!w-full">
          <div className="h-[calc(100vh-100px)] w-100 bg-[#FAFBFD] rounded-2xl p-4 overflow-y-auto">
            <Header
              loadGranularity={loadGranularity}
              onGranularityChange={setLoadGranularity}
              selectedLabel={
                loadGranularity === "Circle"
                  ? (circle?.label ?? null)
                  : loadGranularity === "Transco"
                    ? (transco?.label ?? null)
                    : loadGranularity === "Discom Substation"
                      ? (discom?.label ?? null)
                      : loadGranularity === "Feeder"
                        ? (feeder?.label ?? null)
                        : null
              }
              onTitleClick={(() => {
                if (loadGranularity === "Circle" && circle) {
                  return () =>
                    setModalEntity({
                      level: "circle",
                      id: String(circle.value),
                      label: String(circle.label),
                    });
                }
                if (loadGranularity === "Transco" && transco) {
                  return () =>
                    setModalEntity({
                      level: "transco",
                      id: Number(transco.value),
                      label: String(transco.label),
                    });
                }
                if (loadGranularity === "Discom Substation" && discom) {
                  return () =>
                    setModalEntity({
                      level: "discom_sub",
                      id: Number(discom.value),
                      label: String(discom.label),
                    });
                }
                if (loadGranularity === "Feeder" && feeder) {
                  return () =>
                    setModalEntity({
                      level: "feeder",
                      id: Number(feeder.value),
                      label: String(feeder.label),
                    });
                }
                return undefined;
              })()}
            />

            <div className="flex items-end justify-between border-b border-[#DFEAF2] mb-4">
              <div className="flex items-end gap-4">
                {METRIC_TABS.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setMetricTab(tab)}
                    className={`pb-2 text-[11px] font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                      metricTab === tab
                        ? "border-[#14171F] text-[#14171F]"
                        : "border-transparent text-[#6B7280]"
                    }`}
                  >
                    {tab === "Forecast" && forecastDate ? (
                      <>
                        FORECAST{" "}
                        <span style={{ color: "#3d6b74" }}>
                          ({forecastDate.split("-").reverse().join("/")})
                        </span>
                      </>
                    ) : (
                      tab.toUpperCase()
                    )}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 pb-1">
                <SearchBox
                  sortAlphabetically={true}
                  options={circleList}
                  selectedOptions={circle ? [circle] : []}
                  onChange={(o) => {
                    setCircle(o);
                    setTransco(null);
                    setDiscom(null);
                    setFeeder(null);
                  }}
                  placeholder="Search circle..."
                  minWidth={160}
                  isLoading={loadingEntities}
                  isClearable
                />
                {GRANULARITY_LEVELS[loadGranularity] >= 2 && (
                  <SearchBox
                    sortAlphabetically={true}
                    options={transcoList}
                    selectedOptions={transco ? [transco] : []}
                    onChange={(o) => {
                      setTransco(o);
                      setDiscom(null);
                      setFeeder(null);
                    }}
                    placeholder="Search transco substation..."
                    minWidth={190}
                    isLoading={loadingEntities}
                    isClearable
                  />
                )}
                {GRANULARITY_LEVELS[loadGranularity] >= 3 && (
                  <SearchBox
                    sortAlphabetically={true}
                    options={discomList}
                    selectedOptions={discom ? [discom] : []}
                    onChange={(o) => {
                      setDiscom(o);
                      setFeeder(null);
                      if (o) {
                        const raw = allDiscomSubs.find(
                          (d) => d.discom_substation_id === o.value,
                        );
                        if (raw?.transco_substation_id) {
                          const t = transcoList.find(
                            (x) => x.value === raw.transco_substation_id,
                          );
                          if (t) setTransco(t);
                        }
                      }
                    }}
                    placeholder="Search discom substation..."
                    minWidth={190}
                    isLoading={loadingEntities}
                    isClearable
                  />
                )}
                {GRANULARITY_LEVELS[loadGranularity] >= 4 && (
                  <SearchBox
                    sortAlphabetically={true}
                    options={feederList}
                    selectedOptions={feeder ? [feeder] : []}
                    onChange={(o) => {
                      setFeeder(o);
                      if (o) {
                        const rawF = allFeeders.find(
                          (f) => f.feeder_id === o.value,
                        );
                        if (rawF?.discom_substation_id) {
                          const rawD = allDiscomSubs.find(
                            (d) =>
                              d.discom_substation_id ===
                              rawF.discom_substation_id,
                          );
                          if (rawD) {
                            setDiscom({
                              value: rawD.discom_substation_id,
                              label: rawD.discom_substation_name,
                            });
                            if (rawD.transco_substation_id) {
                              const t = transcoList.find(
                                (x) => x.value === rawD.transco_substation_id,
                              );
                              if (t) setTransco(t);
                            }
                          }
                        }
                      }
                    }}
                    placeholder="Search feeder..."
                    minWidth={160}
                    isLoading={loadingEntities}
                    isClearable
                  />
                )}
              </div>
            </div>

            {(() => {
              const isFeederForecast =
                loadGranularity === "Feeder" && metricTab === "Forecast";
              const isFeederActual =
                loadGranularity === "Feeder" && metricTab === "Actual";
              const displayCards = isFeederForecast
                ? metricCards.filter(
                    (c) => c.title === "Energy" || c.title === "Load",
                  )
                : isFeederActual
                  ? metricCards.filter(
                      (c) => c.title !== "Generation" && c.title !== "Net Load",
                    )
                  : metricCards;
              const dataSummaryCard = isFeederActual
                ? (() => {
                    const full = feederMetric?.metrics?.full;
                    const availPct = full?.available_pct ?? null;
                    const zerosPct = full?.zeros_pct ?? null;
                    const fmt = (v: number | null) =>
                      v != null ? Number(v.toFixed(2)) : null;
                    const cols: { label: string; value: number | null }[] = [
                      { label: "Available", value: fmt(availPct) },
                      { label: "Zeroes", value: fmt(zerosPct) },
                    ];
                    return (
                      <div className="relative bg-white rounded-xl border border-[#DFEAF2] p-3 pl-5 flex flex-col gap-2">
                        <div
                          className="absolute left-0 top-[15%] bottom-[15%] w-[4px] rounded-full"
                          style={{
                            background:
                              "linear-gradient(to bottom, #6B728000, #6B7280 40%, #6B7280 60%, #6B728000)",
                          }}
                        />
                        <p className="text-xs font-medium text-[#14171F]">
                          Data Summary
                        </p>
                        <div className="flex justify-between">
                          {cols.map((col, i) => (
                            <React.Fragment key={col.label}>
                              {i > 0 && (
                                <div className="w-px bg-[#DFEAF2] mx-3 self-stretch" />
                              )}
                              <div className="min-w-0">
                                <p className="text-[8px] font-medium text-[#6B7280] uppercase tracking-wide mb-0.5">
                                  {col.label}
                                </p>
                                <p className="text-lg font-medium text-[#14171F] leading-tight">
                                  {col.value == null ? (
                                    <span className="text-[#A3AED0]">—</span>
                                  ) : (
                                    <>
                                      {col.value}{" "}
                                      <span className="text-xs font-normal text-[#6B7280]">
                                        %
                                      </span>
                                    </>
                                  )}
                                </p>
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                        <div className="pt-1">
                          <div className="h-[38px]" />
                        </div>
                      </div>
                    );
                  })()
                : null;
              return (
                <div
                  className={`grid gap-1 mb-4 ${isFeederActual ? "grid-cols-3" : isFeederForecast ? "grid-cols-2" : "grid-cols-4"}`}
                >
                  {loadingCombined
                    ? Array.from({ length: displayCards.length || 4 }).map(
                        (_, i) => <MetricCardSkeleton key={i} />,
                      )
                    : displayCards.map((card) => {
                        if (metricTab !== "Forecast") {
                          return <MetricCard key={card.title} {...card} />;
                        }
                        const fFull = forecastDemand?.metrics.full;
                        const toMw = (
                          mw: string | number | null | undefined,
                        ): number | null => (mw == null ? null : roundMw(mw));
                        const toMwh = (
                          mwh: string | number | null | undefined,
                        ): number | null =>
                          mwh == null ? null : roundMwh(mwh);
                        let fPeak: number | null = null;
                        let fAvg: number | null = null;
                        let pPeak: number | undefined = undefined;
                        let pAvg: number | undefined = undefined;
                        let fPeakWeather = null as ReturnType<
                          typeof toWeatherStats
                        >;
                        let fAvgWeather = null as ReturnType<
                          typeof toWeatherStats
                        >;
                        let fPeakDate: string | undefined;
                        let fPeakSlot: number | null | undefined;

                        if (card.title === "Energy") {
                          fPeak = toMwh(fFull?.total_mwh);
                          fAvg = toMwh(fFull?.avg_mwh);
                          pPeak = toMwh(fFull?.previous_total_mwh) ?? undefined;
                          pAvg =
                            fFull?.previous_avg_mwh != null
                              ? (toMwh(fFull.previous_avg_mwh) ?? undefined)
                              : undefined;
                          fPeakWeather = toWeatherStats(
                            fFull?.previous_avg_weather,
                          );
                          fAvgWeather = toWeatherStats(fFull?.avg_weather);
                          fPeakDate = undefined;
                          fPeakSlot = undefined;
                        } else if (card.title === "Load") {
                          fPeak = toMw(fFull?.peak_mw);
                          fAvg = toMw(fFull?.avg_mw);
                          pPeak = toMw(fFull?.value_at_peak_mw) ?? undefined;
                          pAvg = toMw(fFull?.previous_avg_mw) ?? undefined;
                          fPeakWeather = toWeatherStats(
                            fFull?.previous_day_peak_weather,
                          );
                          fAvgWeather = toWeatherStats(fFull?.peak_weather);
                          fPeakDate = fFull?.peak_date ?? undefined;
                          fPeakSlot = fFull?.peak_slot_48 ?? null;
                        } else if (card.title === "Generation") {
                          fPeak = toMw(fFull?.peak_generation_mw);
                          fAvg = toMw(fFull?.avg_generation_mw);
                          pPeak = toMw(fFull?.value_at_peak_generation_mw) ?? undefined;
                          pAvg = toMw(fFull?.previous_avg_generation_mw) ?? undefined;
                          fPeakWeather = toWeatherStats(
                            fFull?.previous_day_peak_generation_weather,
                          );
                          fAvgWeather = toWeatherStats(
                            fFull?.peak_generation_weather,
                          );
                          fPeakDate = fFull?.peak_generation_date ?? undefined;
                          fPeakSlot = fFull?.peak_generation_slot_48 ?? null;
                        } else if (card.title === "Net Load") {
                          fPeak = toMw(fFull?.peak_net_mw);
                          fAvg = toMw(fFull?.avg_net_mw);
                          pPeak = toMw(fFull?.value_at_peak_net_mw) ?? undefined;
                          pAvg = toMw(fFull?.previous_avg_net_mw) ?? undefined;
                          fPeakWeather = toWeatherStats(
                            fFull?.peak_net_weather,
                          );
                          fAvgWeather = toWeatherStats(
                            fFull?.previous_day_peak_net_weather,
                          );
                          fPeakDate = fFull?.peak_net_date ?? undefined;
                          fPeakSlot = fFull?.peak_net_slot_48 ?? null;
                        }
                        return (
                          <MetricCard
                            key={card.title}
                            {...card}
                            peakValue={fPeak}
                            averageValue={fAvg}
                            peakPrevDay={pPeak}
                            averagePrevDay={pAvg}
                            peakWeather={fPeakWeather}
                            avgWeather={fAvgWeather}
                            peakDate={fPeakDate}
                            peakSlot={fPeakSlot}
                            showPeakWeather
                          />
                        );
                      })}
                  {dataSummaryCard}
                </div>
              );
            })()}

            <div className="flex items-center gap-3 mb-3">
              <span className="text-[9px] font-medium text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
                Load
              </span>
              <div className="flex-1 h-px bg-[#DFEAF2]" />
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              {loadingCombined
                ? Array.from({ length: 3 }).map((_, i) => (
                    <PeakCardSkeleton key={i} />
                  ))
                : peakCards.map((card) => {
                    const bucketKey =
                      card.title === "Morning Peak"
                        ? "morning"
                        : card.title === "Solar Peak"
                          ? "solar"
                          : "evening";
                    const fBucket =
                      metricTab === "Forecast"
                        ? forecastDemand?.metrics?.[
                            bucketKey as "morning" | "solar" | "evening"
                          ]
                        : null;
                    // Forecast: override bucket peak with forecast value + show
                    // prev-day comparison from value_at_peak_mw (same slot, day-before).
                    const fPeakValue =
                      fBucket?.peak_mw != null
                        ? roundMw(fBucket.peak_mw)
                        : null;
                    const fPrevPeak =
                      fBucket?.value_at_peak_mw != null
                        ? roundMw(fBucket.value_at_peak_mw)
                        : null;
                    return (
                      <PeakCard
                        key={card.title}
                        {...card}
                        peakValue={
                          metricTab === "Forecast" ? fPeakValue : card.peakValue
                        }
                        peakBlock={
                          metricTab === "Forecast"
                            ? (fBucket?.peak_slot_48 ?? null)
                            : card.peakBlock
                        }
                        peakDate={
                          metricTab === "Forecast"
                            ? (fBucket?.peak_date ?? null)
                            : card.peakDate
                        }
                        weather={
                          metricTab === "Forecast"
                            ? toWeatherStats(fBucket?.peak_weather)
                            : card.weather
                        }
                        peakPrevDay={
                          metricTab === "Forecast" ? fPrevPeak : null
                        }
                        weatherPrevDay={
                          metricTab === "Forecast"
                            ? toWeatherStats(fBucket?.previous_day_peak_weather)
                            : null
                        }
                        showPrevDay={metricTab === "Forecast"}
                      />
                    );
                  })}
            </div>

            {(loadGranularity === "Circle" ||
              loadGranularity === "Transco" ||
              loadGranularity === "Discom Substation") && (
              <div className="flex gap-4 border-b border-[#DFEAF2] mb-4">
                {RANK_TABS.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setRankTab(tab)}
                    className={`pb-2 text-[11px] font-medium border-b-2 -mb-px transition-colors ${
                      rankTab === tab
                        ? "border-[#14171F] text-[#14171F]"
                        : "border-transparent text-[#6B7280]"
                    }`}
                  >
                    {tab.toUpperCase()}
                  </button>
                ))}
              </div>
            )}

            {loadGranularity !== "Feeder" && (
              <div className="grid grid-cols-2 gap-4">
                {(
                  metricTab === "Forecast" && rankTab === "Demand"
                    ? loadingForecastRank
                    : loadingRank
                ) ? (
                  <>
                    <RankTableSkeleton />
                    <RankTableSkeleton />
                  </>
                ) : (
                  <>
                    <RankTable
                      title={
                        rankTab === "Generation"
                          ? "Top 3 Plant"
                          : `Top 3 ${
                              loadGranularity === "Circle"
                                ? "Transco Substation"
                                : loadGranularity === "Transco"
                                  ? "Discom Substation"
                                  : loadGranularity === "Discom Substation"
                                    ? "Feeder"
                                    : "Substation"
                            }`
                      }
                      dotColor="#1F7A4D"
                      tag={rankTab === "Generation" ? "Generation" : "Load"}
                      rows={rankRows.top}
                      primaryWeather={primaryWeather}
                      onWeatherChange={setPrimaryWeather}
                      loadUnit={loadUnit}
                      energyUnit={energyUnit}
                      onRowClick={
                        rankTab === "Generation"
                          ? (row) => {
                              if (row.entityId == null) return;
                              setModalEntity({
                                level: "plant",
                                id: row.entityId,
                                label: row.entity,
                              });
                            }
                          : loadGranularity === "Discom Substation"
                            ? (row) => {
                                if (row.entityId == null) return;
                                setModalEntity({
                                  level: "feeder",
                                  id: row.entityId,
                                  label: row.entity,
                                });
                              }
                            : loadGranularity === "Circle" ||
                                loadGranularity === "Transco"
                              ? (row) => {
                                  if (row.entityId == null) return;
                                  setModalEntity({
                                    level:
                                      loadGranularity === "Circle"
                                        ? "transco"
                                        : "discom_sub",
                                    id: row.entityId,
                                    label: row.entity,
                                  });
                                }
                              : undefined
                      }
                    />
                    <RankTable
                      title={
                        rankTab === "Generation"
                          ? "Lowest 3 Plant"
                          : `Lowest 3 ${
                              loadGranularity === "Circle"
                                ? "Transco Substation"
                                : loadGranularity === "Transco"
                                  ? "Discom Substation"
                                  : loadGranularity === "Discom Substation"
                                    ? "Feeder"
                                    : "Substation"
                            }`
                      }
                      dotColor="#E25A1F"
                      tag={rankTab === "Generation" ? "Generation" : "Load"}
                      rows={rankRows.bottom}
                      primaryWeather={primaryWeather}
                      onWeatherChange={setPrimaryWeather}
                      loadUnit={loadUnit}
                      energyUnit={energyUnit}
                      onRowClick={
                        rankTab === "Generation"
                          ? (row) => {
                              if (row.entityId == null) return;
                              setModalEntity({
                                level: "plant",
                                id: row.entityId,
                                label: row.entity,
                              });
                            }
                          : loadGranularity === "Discom Substation"
                            ? (row) => {
                                if (row.entityId == null) return;
                                setModalEntity({
                                  level: "feeder",
                                  id: row.entityId,
                                  label: row.entity,
                                });
                              }
                            : loadGranularity === "Circle" ||
                                loadGranularity === "Transco"
                              ? (row) => {
                                  if (row.entityId == null) return;
                                  setModalEntity({
                                    level:
                                      loadGranularity === "Circle"
                                        ? "transco"
                                        : "discom_sub",
                                    id: row.entityId,
                                    label: row.entity,
                                  });
                                }
                              : undefined
                      }
                    />
                  </>
                )}
              </div>
            )}

            {loadGranularity === "Feeder" && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[13px] font-medium text-[#14171F]">
                    Feeder Summary
                  </span>
                  <FilterTab
                    options={FEEDER_VIEW_TABS}
                    selected={[feederView]}
                    onClick={(opt) => setFeederView(opt)}
                    variant="pill-outline"
                  />
                </div>

                {feederView === "Table" &&
                  (loadingFeederQuality ? (
                    <div className="grid grid-cols-2 gap-4">
                      <RankTableSkeleton />
                      <RankTableSkeleton />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <FeederTable
                        title="List of Feeder ≥ 80% Data Availability"
                        dotColor="#1F7A4D"
                        rows={feederQualityRows.high}
                        onRowClick={(row) => {
                          if (row.entityId == null) return;
                          setModalEntity({
                            level: "feeder",
                            id: row.entityId,
                            label: row.entity,
                          });
                        }}
                      />
                      <FeederTable
                        title="List of Feeder < 20% Data Availability"
                        dotColor="#E25A1F"
                        rows={feederQualityRows.low}
                        onRowClick={(row) => {
                          if (row.entityId == null) return;
                          setModalEntity({
                            level: "feeder",
                            id: row.entityId,
                            label: row.entity,
                          });
                        }}
                      />
                    </div>
                  ))}

                {feederView === "Chart" && (
                  <div className="bg-white rounded-xl border border-[#DFEAF2] p-3">
                    <ReMixChart
                      brushIndex={{
                        startIndex: 0,
                        endIndex: Math.max(
                          0,
                          (feederChartData?.data?.length ?? 1) - 1,
                        ),
                      }}
                      data={feederChartData.data}
                      legends={feederChartData.legends}
                      xDataKey="name"
                      unit="kWh"
                      yAxisLabel="kWh"
                      isLoading={loadingFeederChart}
                      chartContainerHeight="230px"
                      onlyTitle
                      onBarClick={(params) => {
                        const row =
                          feederChartRows.find((r) => r.name === params.name) ??
                          feederChartRows[params.dataIndex ?? -1];
                        if (!row) return;
                        const realName =
                          allFeeders.find((f) => f.feeder_id === row.entity_id)
                            ?.feeder_name ?? row.name;
                        setModalEntity({
                          level: "feeder",
                          id: row.entity_id,
                          label: realName,
                        });
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5">
              <Tooltip
                note={<p>Source:</p>}
                text="1. Weather : Open-Meteo&#10;2. Load : Discom&#10;3. Feeder : FDRMS/FOXMS&#10;4. Generation : HKRP Innovations&#10;"
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
      {modalEntity && (
        <div
          className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setModalEntity(null)}
        >
          <div
            className="bg-gray-100 rounded-2xl w-[90vw] max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <NetConsumptionPopup
              embedded={modalEntity}
              onClose={() => setModalEntity(null)}
              initialTab={metricTab}
              forecastDateOverride={
                metricTab === "Forecast" ? forecastDate : null
              }
            />
          </div>
        </div>
      )}
    </>
  );
};

export default JvvnlMetric;
